/**
 * Centralized Cart Count Manager
 * Handles all cart count updates via AJAX calls
 */

class CartCountManager {
  constructor() {
    this.isUpdating = false;
    this.pendingUpdate = false;
    this.lastKnownCount = null;
    this.init();
  }

  init() {
    // Listen for custom events
    window.addEventListener('cart:updated', () => this.updateCartCount());
    window.addEventListener('cart:item-added', () => this.updateCartCount());
    window.addEventListener('cart:item-removed', () => this.updateCartCount());
    
    // Listen for theme's existing cart count events
    window.addEventListener('cartcount:update', (event) => {
      if (event.detail && event.detail.item_count !== undefined) {
        this.renderCartCount(event.detail.item_count);
        this.lastKnownCount = event.detail.item_count;
      }
    });

    // Intercept cart operations that might not trigger theme events
    this.interceptCartOperations();
    
    // Periodic check for cart count changes (fallback)
    this.startPeriodicCheck();
  }

  /**
   * Intercept cart operations that might bypass theme events
   */
  interceptCartOperations() {
    // Override fetch for cart operations
    const originalFetch = window.fetch;
    window.fetch = function(input, init) {
      return originalFetch(input, init).then(response => {
        const url = typeof input === 'string' ? input : input?.url;
        
        // Check if this is a cart mutation request
        if (url && window.cartCountManager && window.cartCountManager.isCartMutationUrl(url)) {
          // Delay update to ensure server state is consistent
          setTimeout(() => {
            window.cartCountManager.updateCartCount();
          }, 200);
        }
        
        return response;
      });
    };

    // Override jQuery AJAX for cart operations
    if (window.$ && window.$.ajaxSetup) {
      $(document).ajaxComplete(function(event, xhr, settings) {
        if (settings.url && window.cartCountManager && window.cartCountManager.isCartMutationUrl(settings.url)) {
          setTimeout(() => {
            window.cartCountManager.updateCartCount();
          }, 200);
        }
      });
    }
  }

  /**
   * Check if URL is a cart mutation endpoint
   */
  isCartMutationUrl(url) {
    const cartMutationEndpoints = [
      '/cart/add',
      '/cart/change', 
      '/cart/update',
      '/cart/clear'
    ];
    
    // Exclude /cart.js to prevent infinite loops
    if (url.includes('/cart.js')) {
      return false;
    }
    
    return cartMutationEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * Start periodic check for cart count changes (fallback mechanism)
   */
  startPeriodicCheck() {
    // Check every 3 seconds, but only if cart count might have changed
    setInterval(() => {
      // Only check if we suspect the count might be stale
      if (this.shouldCheckCartCount()) {
        this.updateCartCount();
      }
    }, 3000);
  }

  /**
   * Determine if we should check cart count
   */
  shouldCheckCartCount() {
    // Check if we're on cart page or if there are cart operations happening
    return window.location.pathname.includes('/cart') || 
           document.querySelector('.cart-item-list') !== null ||
           document.querySelector('[data-cartitem-key]') !== null;
  }

  /**
   * Update cart count via AJAX - the single source of truth
   */
  async updateCartCount() {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      this.pendingUpdate = true;
      return;
    }

    this.isUpdating = true;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Only update if count actually changed
      if (this.lastKnownCount !== cart.item_count) {
        this.renderCartCount(cart.item_count);
        this.lastKnownCount = cart.item_count;
        
        // Dispatch event for other components that might need cart data
        window.dispatchEvent(new CustomEvent('cartcount:updated', { 
          detail: { count: cart.item_count, cart: cart }
        }));
      }

    } catch (error) {
      console.error('Failed to update cart count:', error);
    } finally {
      this.isUpdating = false;
      
      // Handle any pending update request
      if (this.pendingUpdate) {
        this.pendingUpdate = false;
        setTimeout(() => this.updateCartCount(), 100);
      }
    }
  }

  /**
   * Render cart count in all relevant elements
   */
  renderCartCount(count) {
    // Primary cart count element (main header)
    const primaryElements = document.querySelectorAll('.site-header-cart--count');
    
    primaryElements.forEach(el => {
      el.setAttribute('data-header-cart-count', count);
      
      if (count > 0) {
        el.classList.add('visible');
      } else {
        el.classList.remove('visible');
      }
    });

    // Secondary cart count elements (mobile, drawer, etc.)
    const secondaryElements = document.querySelectorAll(
      '.cart-count, [data-cart-count], .cart-link__bubble-num, #cart-icon-bubble'
    );
    
    secondaryElements.forEach(el => {
      // Skip if this is already handled by primary elements
      if (el.classList.contains('site-header-cart--count')) return;
      
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-block' : 'none';
      
      if (count > 0) {
        el.classList.add('visible');
      } else {
        el.classList.remove('visible');
      }
    });
  }

  /**
   * Manual trigger for cart count update
   */
  refresh() {
    this.updateCartCount();
  }
}

// Initialize the cart count manager
document.addEventListener('DOMContentLoaded', () => {
  window.cartCountManager = new CartCountManager();
});

// Global helper function for backward compatibility
window.updateCartCount = function() {
  window.cartCountManager?.refresh();
};
// Initialize the cart count manager
document.addEventListener('DOMContentLoaded', () => {
  window.cartCountManager = new CartCountManager();
  
  // Additional cart page specific handlers
  if (window.location.pathname.includes('/cart')) {
    // Monitor cart quantity changes on cart page
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if cart items were added/removed/modified
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && mutation.attributeName === 'value')) {
          const target = mutation.target;
          if (target.matches && (
              target.matches('[data-quantity-input]') ||
              target.matches('.quantity-selector__input') ||
              target.matches('.cart-item-list') ||
              target.closest('.cart-item-list')
            )) {
            shouldUpdate = true;
          }
        }
      });
      
      if (shouldUpdate) {
        setTimeout(() => {
          window.cartCountManager.updateCartCount();
        }, 500);
      }
    });
    
    // Start observing cart changes
    const cartContainer = document.querySelector('.cart-main, .cart-container, .cart-items, .bdySctn');
    if (cartContainer) {
      observer.observe(cartContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'data-quantity']
      });
    }
  }
});

// Global helper function for backward compatibility
window.updateCartCount = function() {
  window.cartCountManager?.refresh();
};